// Notification Service - Backend-driven notification management
import { pollNotifications, markNotificationRead, markAllNotificationsRead, deleteNotificationsByContact, PollNotification } from './ContactService';
import SessionService from './SessionService';

export interface Notification {
    id: number;
    contactId: number;
    contactName: string;
    contactNumber: string;
    sectorId: number;
    message: string;
    timestamp: string;
    isRead: boolean;
}

class NotificationService {
    private notifications: Notification[] = [];
    private pollInterval: ReturnType<typeof setInterval> | null = null;
    private currentSectorId: number | null = null;

    // Update notifications from backend poll data
    public updateFromPoll(pollData: PollNotification[]): void {
        this.notifications = pollData.map(n => ({
            id: n.id,
            contactId: n.contactId,
            contactName: n.title,
            contactNumber: '',
            sectorId: n.sectorId,
            message: n.message,
            timestamp: n.createdAt,
            isRead: n.isRead,
        }));
        this.dispatchUpdate();
    }

    // Mark a single notification as read (backend + local)
    public async markAsRead(id: number): Promise<void> {
        // Optimistic update
        const notification = this.notifications.find(n => n.id === id);
        if (notification) {
            notification.isRead = true;
            this.dispatchUpdate();
        }
        // Persist to backend
        await markNotificationRead(id);
    }

    // Mark all notifications as read (backend + local)
    public async markAllAsRead(): Promise<void> {
        const sectorId = this.currentSectorId || Number(SessionService.getSectorId());
        if (!sectorId) return;

        // Optimistic update
        this.notifications.forEach(n => n.isRead = true);
        this.dispatchUpdate();

        // Persist to backend
        await markAllNotificationsRead(sectorId);
    }

    // Remove notifications for a contact (backend + local)
    public async removeByContactId(contactId: number): Promise<void> {
        // Optimistic update
        this.notifications = this.notifications.filter(n => n.contactId !== contactId);
        this.dispatchUpdate();

        // Persist to backend
        await deleteNotificationsByContact(contactId);
    }

    public getAll(): Notification[] {
        return this.notifications.map(n => ({ ...n }));
    }

    public getUnreadCount(): number {
        return this.notifications.filter(n => !n.isRead).length;
    }

    // Start polling for a sector
    public startPolling(sectorId: number): void {
        this.stopPolling();
        this.currentSectorId = sectorId;

        const poll = async () => {
            try {
                const result = await pollNotifications(sectorId);
                if (result.notifications) {
                    this.updateFromPoll(result.notifications);
                }
            } catch {
                // Silent fail
            }
        };

        poll(); // Initial fetch
        this.pollInterval = setInterval(poll, 5000);
    }

    // Stop polling
    public stopPolling(): void {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    }

    /** Força um poll imediato (ex.: após human_transfer_notification via WebSocket). */
    public async refreshNow(): Promise<void> {
        const sectorId = this.currentSectorId || Number(SessionService.getSectorId());
        if (!sectorId) return;
        try {
            const result = await pollNotifications(sectorId);
            if (result.notifications) {
                this.updateFromPoll(result.notifications);
            }
        } catch {
            // Silent fail
        }
    }

    private dispatchUpdate(): void {
        window.dispatchEvent(new CustomEvent('notificationUpdate', {
            detail: { notifications: this.notifications.map(n => ({ ...n })) }
        }));
    }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
